// Content Composition Canvas — LIGHT brand presets (Post Your Content's light/teal visual language).
// No dark backgrounds, no black overlays. White / light-grey surfaces, teal accents,
// charcoal text, Montserrat. Presets pull accent/font/logo from the workspace brand config.

export const PLATFORM_SIZES = {
  "1:1": { w: 1080, h: 1080, label: "Square 1:1 · Instagram" },
  "4:5": { w: 1080, h: 1350, label: "Portrait 4:5 · Instagram" },
  "16:9": { w: 1200, h: 675, label: "Landscape 16:9 · LinkedIn / Facebook" },
};

export const DEFAULT_BRAND = {
  accent: "#5FA8B4",
  deep_teal: "#3A7A8C",
  charcoal: "#3C4048",
  slate: "#7A9090",
  light_grey: "#F5F5F5",
  white: "#FFFFFF",
  font: "Montserrat",
  logo_url: null,
  logo_text: "YOUR BRAND",
};

export const PRESETS = [
  { key: "clean_professional", name: "Clean Professional", desc: "White canvas, bold headline, teal accent line." },
  { key: "clean_educational", name: "Clean Educational", desc: "Image with teal text block at the bottom." },
  { key: "stat_quote", name: "Stat / Quote", desc: "Light background, large centred number or quote." },
  { key: "carousel_slide", name: "Carousel Slide", desc: "Teal header bar with slide number + title." },
];

let _id = 0;
export const uid = (p = "l") => `${p}_${Date.now().toString(36)}_${_id++}`;

const swatchList = (b) => [b.white, b.light_grey, b.accent, b.deep_teal, b.charcoal, b.slate];
export const brandSwatches = swatchList;

// Build a fresh document (background + layers) for a preset at a given platform size.
export function buildPreset(key, brand, size) {
  const b = { ...DEFAULT_BRAND, ...(brand || {}) };
  const { w, h } = size;
  const m = Math.round(w * 0.07);
  const logo = {
    id: uid("logo"),
    type: "text",
    role: "logo",
    text: b.logo_text || "YOUR BRAND",
    fontFamily: b.font,
    fontWeight: "700",
    letterSpacing: 2,
    fill: b.accent,
  };

  if (key === "clean_educational") {
    const blockY = Math.round(h * 0.6);
    const blockH = h - blockY;
    return {
      preset: key,
      background: { type: "color", color: b.light_grey, imageUrl: null },
      layers: [
        { ...logo, x: m, y: m, width: w - 2 * m, fontSize: Math.round(w * 0.026), align: "left" },
        { id: uid("rect"), type: "rect", x: 0, y: blockY, width: w, height: blockH, fill: b.accent, cornerRadius: 0, opacity: 1, stroke: "", strokeWidth: 0 },
        { id: uid("text"), type: "text", text: "The technique that actually resolves it", x: m, y: blockY + Math.round(blockH * 0.16), width: w - 2 * m, fontSize: Math.round(w * 0.05), fontWeight: "800", fill: b.white, align: "left", lineHeight: 1.12, letterSpacing: 0, opacity: 1 },
        { id: uid("text"), type: "text", text: "A short, specific line explaining the approach or benefit.", x: m, y: blockY + Math.round(blockH * 0.16) + Math.round(w * 0.13), width: w - 2 * m, fontSize: Math.round(w * 0.028), fontWeight: "400", fill: b.white, align: "left", lineHeight: 1.3, letterSpacing: 0, opacity: 0.92 },
      ],
    };
  }

  if (key === "stat_quote") {
    return {
      preset: key,
      background: { type: "color", color: b.light_grey, imageUrl: null },
      layers: [
        { id: uid("text"), type: "text", text: "98%", x: m, y: Math.round(h * 0.3), width: w - 2 * m, fontSize: Math.round(w * 0.16), fontWeight: "900", fill: b.accent, align: "center", lineHeight: 1, letterSpacing: -1, opacity: 1 },
        { id: uid("text"), type: "text", text: "of customers would recommend us.", x: m, y: Math.round(h * 0.3) + Math.round(w * 0.19), width: w - 2 * m, fontSize: Math.round(w * 0.034), fontWeight: "400", fill: b.slate, align: "center", lineHeight: 1.3, letterSpacing: 0, opacity: 1 },
        { ...logo, x: m, y: h - m - Math.round(w * 0.028), width: w - 2 * m, fontSize: Math.round(w * 0.026), align: "center" },
      ],
    };
  }

  if (key === "carousel_slide") {
    const barH = Math.round(h * 0.15);
    return {
      preset: key,
      background: { type: "color", color: b.white, imageUrl: null },
      layers: [
        { id: uid("rect"), type: "rect", x: 0, y: 0, width: w, height: barH, fill: b.accent, cornerRadius: 0, opacity: 1, stroke: "", strokeWidth: 0 },
        { id: uid("text"), type: "text", text: "5 tips to get started", x: m, y: Math.round(barH * 0.32), width: Math.round(w * 0.6), fontSize: Math.round(w * 0.04), fontWeight: "800", fill: b.white, align: "left", lineHeight: 1.1, letterSpacing: 0, opacity: 1 },
        { id: uid("text"), type: "text", text: "1 / 5", x: w - m - Math.round(w * 0.2), y: Math.round(barH * 0.34), width: Math.round(w * 0.2), fontSize: Math.round(w * 0.035), fontWeight: "700", fill: b.white, align: "right", lineHeight: 1, letterSpacing: 1, opacity: 1 },
        { id: uid("text"), type: "text", text: "Body content goes here. Keep it to one clear idea per slide with plenty of breathing room.", x: m, y: Math.round(h * 0.24), width: w - 2 * m, fontSize: Math.round(w * 0.036), fontWeight: "400", fill: b.charcoal, align: "left", lineHeight: 1.4, letterSpacing: 0, opacity: 1 },
        { ...logo, x: m, y: h - m - Math.round(w * 0.026), width: w - 2 * m, fontSize: Math.round(w * 0.024), align: "right" },
      ],
    };
  }

  // Default: clean_professional
  const hlFs = Math.round(w * 0.072);
  const hlY = Math.round(h * 0.13);
  const lineY = hlY + Math.round(hlFs * 2.4);
  return {
    preset: "clean_professional",
    background: { type: "color", color: b.white, imageUrl: null },
    layers: [
      { id: uid("text"), type: "text", text: "The problem that won't go away", x: m, y: hlY, width: w - 2 * m, fontSize: hlFs, fontWeight: "800", fill: b.charcoal, align: "left", lineHeight: 1.1, letterSpacing: 0, opacity: 1 },
      { id: uid("rect"), type: "rect", x: m, y: lineY, width: Math.round(w * 0.16), height: Math.max(4, Math.round(w * 0.008)), fill: b.accent, cornerRadius: 0, opacity: 1, stroke: "", strokeWidth: 0 },
      { id: uid("text"), type: "text", text: "A short, specific line about the problem you solve.", x: m, y: lineY + Math.round(h * 0.03), width: Math.round(w * 0.72), fontSize: Math.round(w * 0.032), fontWeight: "400", fill: b.charcoal, align: "left", lineHeight: 1.35, letterSpacing: 0, opacity: 0.9 },
      { ...logo, x: m, y: h - m - Math.round(w * 0.028), width: w - 2 * m, fontSize: Math.round(w * 0.026), align: "right" },
    ],
  };
}

export function emptyDoc(brand, size) {
  const b = { ...DEFAULT_BRAND, ...(brand || {}) };
  return { preset: null, background: { type: "color", color: b.white, imageUrl: null }, layers: [] };
}

// Proportionally rescale layers when switching platform size.
export function rescaleLayer(l, rx, ry) {
  const r = (rx + ry) / 2;
  const out = { ...l };
  if (typeof l.x === "number") out.x = Math.round(l.x * rx);
  if (typeof l.y === "number") out.y = Math.round(l.y * ry);
  if (typeof l.width === "number") out.width = Math.round(l.width * rx);
  if (typeof l.height === "number") out.height = Math.round(l.height * ry);
  if (typeof l.fontSize === "number") out.fontSize = Math.max(8, Math.round(l.fontSize * r));
  if (typeof l.strokeWidth === "number") out.strokeWidth = Math.round(l.strokeWidth * r);
  if (typeof l.cornerRadius === "number") out.cornerRadius = Math.round(l.cornerRadius * r);
  if (Array.isArray(l.points)) out.points = l.points.map((p, i) => (i % 2 === 0 ? p * rx : p * ry));
  return out;
}
